import { publicAsset } from '../lib/assets';
import { createEl } from '../lib/dom';

interface Partner {
  name: string;
  org: string;
  location: string;
  /** Imagen circular; si falta, se usa {@link avatarIcon}. */
  logoSrc?: string;
  logoAlt: string;
  /** Wordmark / logo horizontal: no recortar en el círculo. */
  logoFit?: 'contain';
  /** Avatar sin foto (p. ej. colaborador individual). */
  avatarIcon?: 'person';
  services: readonly string[];
}

const PARTNERS: Partner[] = [
  {
    name: 'Los Cordiales',
    org: 'Empresa de taxis en Palmira',
    location: 'Palmira, Valle del Cauca',
    logoSrc: publicAsset('partners/los-cordiales-logo.png'),
    logoAlt: 'Los Cordiales — Empresa de taxis en Palmira',
    services: ['Conductor elegido', 'Asistencia gasolina', 'Asistencia batería'],
  },
  {
    name: 'Auto Gruas Deprisa S.A.S',
    org: 'Empresa de gruas en la ciudad de Bogota',
    location: 'Bogota DC',
    logoSrc: publicAsset('partners/auto-gruas-deprisa.png'),
    logoAlt: 'Auto Gruas Deprisa S.A.S — Empresa de gruas en Bogotá',
    services: ['Gruas carros livianos', 'Gruas carros pesados', 'Gruas motos'],
  },
  {
    name: 'Gruas Rodriguez',
    org: 'Empresa de gruas y servicios asistenciales de bogota',
    location: 'Bogota DC',
    logoSrc: publicAsset('partners/gruas-rodriguez.png'),
    logoAlt: 'Gruas Rodriguez — Empresa de gruas y servicios asistenciales en Bogotá',
    services: [
      'Gruas carros livianos',
      'Gruas carros pesados',
      'Gruas motos',
      'Conductor elegido',
      'Carro taller',
      'Asistencia de bateria',
      'Asistencia de gasolina',
    ],
  },
  {
    name: 'Gruas Laf',
    org: 'Empresa de gruas en Curanilahue',
    location: 'Curanilahue, Chile',
    logoSrc: publicAsset('partners/gruas-laf.png'),
    logoAlt: 'Gruas Laf — Empresa de gruas en Curanilahue, Chile',
    services: ['Servicios de gruas', 'Asistencia en ruta', 'Rescate'],
  },
  {
    name: 'Gruas Emilio',
    org: 'Servicio de grua en Valparaiso',
    location: 'Valparaiso, Chile',
    logoSrc: publicAsset('partners/gruas-emilio.png'),
    logoAlt: 'Gruas Emilio — Servicio de grua en Valparaíso, Chile',
    services: [
      'Servicio de grua para vehiculos',
      'Servicio de grua, rescates y traslados',
    ],
  },
  {
    name: 'ConduceXTi',
    org: 'Servicio de conductor elegido y asistencia por bateria',
    location: 'Cartagena de Indias, Colombia',
    logoSrc: publicAsset('partners/conducexti.png'),
    logoAlt: 'ConduceXTi — Conductor elegido y asistencia en Cartagena',
    services: ['Paso Corriente', 'Conductor Elegido Cartagena, Bolivar'],
  },
  {
    name: 'Jhon Cuartas',
    org: 'Servicio de conductor elegido en Medellin',
    location: 'Medellin, Colombia',
    avatarIcon: 'person',
    logoAlt: 'Jhon Cuartas — Conductor elegido en Medellín',
    services: [
      'Si no puedes conducir o transportar algún elemento puedo realizarlo con total transparencia, actitud y honestidad.',
    ],
  },
  {
    name: 'Abrahan',
    org: 'Servicios varios de asistencia vehicular en todo Medellin',
    location: 'Medellin, Colombia',
    logoSrc: publicAsset('partners/abrahan.png'),
    logoAlt: 'Abrahan — Asistencia vehicular en Medellín',
    services: [
      'Inicio vehiculos con cable',
      'Cambios de aceite, refrigerante, liquido de frenos y direccion',
      'Gasolina',
      'Mecanico, electricista y electronico',
    ],
  },
  {
    name: 'Yaison González',
    org: 'Servicios electronico y bateria en Bogota',
    location: 'Bogota, Colombia',
    logoSrc: publicAsset('partners/yaison-gonzalez.png'),
    logoAlt: 'Yaison González — Electricidad automotriz y batería en Bogotá',
    services: [
      'Electricidad automotriz',
      'Servicio de bateria',
      'Domicilio',
    ],
  },
  {
    name: 'ASISTIGROUP',
    org: 'Empresa de Gruas en la ciudad de Bogota',
    location: 'Bogota, Colombia',
    logoSrc: publicAsset('partners/asistigroup.png'),
    logoAlt: 'ASISTIGROUP — Empresa de grúas en Bogotá',
    services: ['Grua', 'Grua moto', 'Grua vehiculo liviano'],
  },
  {
    name: 'Gruas Muñoz',
    org: 'Empresa de Gruas en Cali',
    location: 'Santiago de Cali, Colombia',
    logoSrc: publicAsset('partners/gruas-munoz.png'),
    logoAlt: 'Gruas Muñoz — Empresa de grúas en Cali',
    services: ['Grua para motos'],
  },
  {
    name: 'LAVACARVIP',
    org: 'Lavado de vehículos a domicilio. Cuidamos tu carro como se merece. Servicio puntual, productos de calidad y resultados premium.',
    location: 'Cali, Valle del Cauca, Colombia',
    logoSrc: publicAsset('partners/lavacarvip.jpg'),
    logoAlt: 'LAVACARVIP — Lavados a domicilio en Cali',
    logoFit: 'contain',
    services: [
      'Lavado de vehículos Combos VIP',
      'Lavado básico',
      'Multi combos de lavado',
    ],
  },
];

function renderPersonAvatar(alt: string): HTMLElement {
  const wrap = createEl('div', {
    className: 'sv-partners__logo sv-partners__logo--icon',
    role: 'img',
    'aria-label': alt,
  });
  wrap.innerHTML = `
    <svg class="sv-partners__person-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v1.2c0 .7.5 1.2 1.2 1.2h16.8c.7 0 1.2-.5 1.2-1.2v-1.2c0-3.2-6.4-4.8-9.6-4.8z"/>
    </svg>
  `;
  return wrap;
}

function renderPartnerAvatar(partner: Partner): HTMLElement {
  if (partner.logoSrc) {
    const className =
      partner.logoFit === 'contain'
        ? 'sv-partners__logo sv-partners__logo--contain'
        : 'sv-partners__logo';
    return createEl('img', {
      className,
      src: partner.logoSrc,
      alt: partner.logoAlt,
      width: '80',
      height: '80',
      decoding: 'async',
      loading: 'lazy',
    });
  }
  return renderPersonAvatar(partner.logoAlt);
}

function renderPartnerCard(partner: Partner): HTMLElement {
  const services = createEl('ul', { className: 'sv-partners__services' });
  for (const label of partner.services) {
    const chipClass =
      label.length > 48 ? 'sv-partners__chip sv-partners__chip--long' : 'sv-partners__chip';
    services.appendChild(createEl('li', { className: chipClass }, [label]));
  }

  return createEl('article', { className: 'sv-partners__card' }, [
    renderPartnerAvatar(partner),
    createEl('div', { className: 'sv-partners__body' }, [
      createEl('h3', { className: 'sv-partners__name' }, [partner.name]),
      createEl('p', { className: 'sv-partners__meta' }, [partner.org]),
      createEl('p', { className: 'sv-partners__location' }, [partner.location]),
      services,
    ]),
  ]);
}

/**
 * Banner horizontal de clientes y colaboradores (scroll / marquee).
 */
export function renderPartnersBanner(target: HTMLElement): void {
  const trackInner = createEl('div', { className: 'sv-partners__track-inner' });
  for (const partner of PARTNERS) {
    trackInner.appendChild(renderPartnerCard(partner));
  }
  // Duplicado para marquee continuo cuando hay varios.
  for (const partner of PARTNERS) {
    const clone = renderPartnerCard(partner);
    clone.setAttribute('aria-hidden', 'true');
    trackInner.appendChild(clone);
  }

  const section = createEl(
    'section',
    {
      className: 'sv-section sv-section--surface sv-section--tight sv-partners',
      id: 'clientes-colaboradores',
      'aria-labelledby': 'partners-title',
    },
    [
      createEl('div', { className: 'sv-inner' }, [
        createEl('h2', { className: 'sv-section__title', id: 'partners-title' }, [
          'Nuestros Colaboradores',
        ]),
        createEl(
          'div',
          {
            className: 'sv-partners__banner',
            role: 'region',
            'aria-label': 'Colaboradores en Servave',
          },
          [createEl('div', { className: 'sv-partners__track' }, [trackInner])]
        ),
      ]),
    ]
  );

  target.appendChild(section);
}
